import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiErrorDetail } from "../../lib/api";

const RELAY_ENDPOINT = { admin: "/admin/relay/send", manager: "/manager/relay/send", affiliate: "/rep/relay/send" };

export function RelayComposer({ open, onClose, replyTo }) {
  const { user } = useAuth();
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [targetType, setTargetType] = useState("");
  const [targetId, setTargetId] = useState("");
  const [reps, setReps] = useState([]);
  const [managers, setManagers] = useState([]);
  const [managerName, setManagerName] = useState("your manager");

  useEffect(() => {
    if (!open) return;
    setMessage("");
    if (replyTo) return;
    setTargetType("");
    setTargetId("");
    if (user?.role === "admin") {
      api.get("/admin/users").then(({ data }) => {
        setReps(data.filter((u) => u.role === "affiliate"));
        setManagers(data.filter((u) => u.role === "manager"));
      }).catch(() => {});
    } else if (user?.role === "manager") {
      api.get("/manager/affiliates").then(({ data }) => setReps(data)).catch(() => {});
    } else if (user?.role === "affiliate") {
      api.get("/rep/dashboard-summary").then(({ data }) => setManagerName(data.manager_display_name || "your manager")).catch(() => {});
    }
  }, [open, replyTo, user?.role]);

  const send = async (e) => {
    e.preventDefault();
    if (!message.trim() || sending) return;
    const needsTarget = !replyTo && user?.role !== "affiliate";
    if (needsTarget && (!targetType || (targetType !== "admin" && !targetId))) {
      toast.error("Choose who this message goes to");
      return;
    }
    setSending(true);
    try {
      const payload = replyTo
        ? { message, reply_to_id: replyTo.id }
        : needsTarget
          ? { message, target_type: targetType, target_id: targetType === "admin" ? undefined : targetId }
          : { message };
      const { data } = await api.post(RELAY_ENDPOINT[user.role], payload);
      toast.success(data.message || "Message sent");
      setMessage("");
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const title = replyTo ? `Reply to ${replyTo.from_name}` : user?.role === "affiliate" ? `Message ${managerName}` : "Send a Message";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream" data-testid="relay-composer-modal">
        <DialogHeader><DialogTitle className="font-serif">{title}</DialogTitle></DialogHeader>
        <form onSubmit={send} className="space-y-3">
          {!replyTo && user?.role === "admin" && (
            <div className="grid grid-cols-2 gap-2">
              <Select value={targetType} onValueChange={(v) => { setTargetType(v); setTargetId(""); }}>
                <SelectTrigger data-testid="relay-target-type-select" title="Choose who this message goes to — a specific rep, a specific manager, or the whole team." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                  <SelectValue placeholder="Send to..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rep" data-testid="relay-target-type-rep">A Rep</SelectItem>
                  <SelectItem value="manager" data-testid="relay-target-type-manager">A Manager</SelectItem>
                  <SelectItem value="team" data-testid="relay-target-type-team">Whole Team</SelectItem>
                </SelectContent>
              </Select>
              {targetType && (
                <Select value={targetId} onValueChange={setTargetId}>
                  <SelectTrigger data-testid="relay-target-id-select" title="Pick the specific person to send this message to." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                    <SelectValue placeholder="Choose..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(targetType === "rep" ? reps : managers).map((u) => (
                      <SelectItem key={u.id} value={u.id} data-testid={`relay-target-option-${u.id}`}>{u.display_name} ({u.user_id})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
          {!replyTo && user?.role === "manager" && (
            <div className="grid grid-cols-2 gap-2">
              <Select value={targetType} onValueChange={(v) => { setTargetType(v); setTargetId(""); }}>
                <SelectTrigger data-testid="relay-target-type-select" title="Send this to one of your reps, or up to Wes (Admin)." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                  <SelectValue placeholder="Send to..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rep" data-testid="relay-target-type-rep">One of My Reps</SelectItem>
                  <SelectItem value="admin" data-testid="relay-target-type-admin">Wes (Admin)</SelectItem>
                </SelectContent>
              </Select>
              {targetType === "rep" && (
                <Select value={targetId} onValueChange={setTargetId}>
                  <SelectTrigger data-testid="relay-target-id-select" title="Pick which rep on your team receives this message." className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                    <SelectValue placeholder="Choose rep..." />
                  </SelectTrigger>
                  <SelectContent>
                    {reps.map((r) => (
                      <SelectItem key={r.id} value={r.id} data-testid={`relay-target-option-${r.id}`}>{r.display_name} ({r.user_id})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
          <Textarea
            data-testid="relay-message-textarea"
            required
            autoFocus
            placeholder="Type your message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            title="The text of your message."
            className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream min-h-28"
          />
          <Button data-testid="relay-send-button" type="submit" disabled={sending} title="Sends this message." className="w-full bg-igc-gold hover:bg-igc-goldHover text-black">
            {sending ? "Sending…" : "Send"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
